import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCurrentUserId, selectLoggedIn } from 'selectors/accountSelectors'
import requestMyEnrollments from 'api/loyalty/requestMyEnrollments'
import type { MyEnrollment } from 'api/loyalty/requestMyEnrollments'
import type { StatusMatchLabPayload } from 'loyalty/statusMatch/statusMatchTypes'
import { useActiveStatusMatchCampaigns } from './useActiveStatusMatchCampaign'

export interface MergedStep {
  key: string
  title: string
  description: string
  eligible: boolean
}

export interface StatusMatchEnrollmentState {
  enrollment: MyEnrollment
  config: StatusMatchLabPayload
  steps: Array<MergedStep>
}

export interface MyStatusMatchEnrollmentResult {
  /** @deprecated Prefer enrollmentStates — enrollment for campaignId when provided */
  enrollment: MyEnrollment | undefined
  /** @deprecated Prefer enrollmentStates — config for campaignId when provided */
  config: StatusMatchLabPayload | undefined
  /** @deprecated Prefer enrollmentStates — steps for campaignId when provided */
  steps: Array<MergedStep>
  /** All enrollments matched to active Lab campaigns in the user's region */
  enrollmentStates: Array<StatusMatchEnrollmentState>
  /** True when enrolled in campaignId (if provided), otherwise any active enrollment */
  isEnrolled: boolean
  isLoading: boolean
  /** True once enrollments were fetched successfully for the current customer — false before the first fetch and after a failure */
  isLoaded: boolean
  refetch: () => void
}

function mergeEnrollmentSteps(
  enrollment: MyEnrollment,
  config: StatusMatchLabPayload,
): Array<MergedStep> {
  if (!config.successSteps) return []
  return config.successSteps.map(step => {
    const check = enrollment.checks.find(c => c.key === step.key)
    return {
      key: step.key,
      title: step.title,
      description: step.description,
      eligible: check?.eligible ?? false,
    }
  })
}

/**
 * Fetches the user's active enrollments for all Status Match campaigns in their region.
 * Pass campaignId to scope legacy fields and isEnrolled to a single campaign (e.g. landing pages).
 */
function useMyStatusMatchEnrollment(campaignId?: string): MyStatusMatchEnrollmentResult {
  const isLoggedIn = useAppSelector(state => selectLoggedIn(state))
  const customerId = useAppSelector(state => getCurrentUserId(state))
  const activeCampaigns = useActiveStatusMatchCampaigns()
  const [enrollments, setEnrollments] = useState<Array<MyEnrollment>>([])
  const [isLoading, setIsLoading] = useState(false)
  const [loadedCustomerId, setLoadedCustomerId] = useState<string>()

  const fetch = useCallback(async() => {
    if (!isLoggedIn || !customerId || activeCampaigns.length === 0) return
    setIsLoading(true)
    try {
      const result = await requestMyEnrollments(customerId)
      setEnrollments(result)
      setLoadedCustomerId(customerId)
    } catch {
      setEnrollments([])
      setLoadedCustomerId(undefined)
    } finally {
      setIsLoading(false)
    }
  }, [isLoggedIn, customerId, activeCampaigns.length])

  useEffect(() => {
    fetch()
  }, [fetch])

  const enrollmentStates = useMemo<Array<StatusMatchEnrollmentState>>(() => {
    return activeCampaigns.flatMap(campaign => {
      const enrollment = enrollments.find(e => e.campaignId === campaign.config.campaignId)
      if (!enrollment) return []
      return [{
        enrollment,
        config: campaign.config,
        steps: mergeEnrollmentSteps(enrollment, campaign.config),
      }]
    })
  }, [activeCampaigns, enrollments])

  const enrollmentForCampaign = useMemo(
    () => campaignId
      ? enrollmentStates.find(state => state.config.campaignId === campaignId)
      : undefined,
    [enrollmentStates, campaignId],
  )

  return {
    enrollment: enrollmentForCampaign?.enrollment,
    config: enrollmentForCampaign?.config,
    steps: enrollmentForCampaign?.steps ?? [],
    enrollmentStates,
    isEnrolled: campaignId ? !!enrollmentForCampaign : enrollmentStates.length > 0,
    isLoading,
    isLoaded: !!customerId && loadedCustomerId === customerId,
    refetch: fetch,
  }
}

export default useMyStatusMatchEnrollment
